import { useColorScheme } from "@mui/material";
import { useLocalStorageValue } from "@react-hookz/web";
import { useCallback, useMemo } from "react";

export enum ThemeMode {
  Preset = "preset",
  System = "system",
  Light = "light",
  Dark = "dark",
}

export type ResolvedThemeMode = ThemeMode.Light | ThemeMode.Dark;

export type UseGlobalTheme = {
  /** The current theme mode */
  mode: ThemeMode;
  /** Set the theme mode */
  setTheme(mode: ThemeMode): void;
};

export const STORAGE_KEY = "Shuttlerock:GlobalThemeMode";

type ResolveThemeModeOptions = {
  presetMode?: ResolvedThemeMode;
  systemMode?: ResolvedThemeMode;
};

const toProviderMode = (mode: ThemeMode): "light" | "dark" | "system" => {
  switch (mode) {
    case ThemeMode.System:
      return "system";
    case ThemeMode.Dark:
      return "dark";
    case ThemeMode.Light:
    case ThemeMode.Preset:
      return "light";
  }
};

const DARK_MEDIA_QUERY = "(prefers-color-scheme: dark)";

const readBootstrappedColorScheme = (): ResolvedThemeMode | null => {
  if (typeof document === "undefined") {
    return null;
  }

  if (document.documentElement.hasAttribute("data-dark")) {
    return ThemeMode.Dark;
  }

  if (document.documentElement.hasAttribute("data-light")) {
    return ThemeMode.Light;
  }

  return null;
};

const prefersDarkColorScheme = (): boolean => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }

  return window.matchMedia(DARK_MEDIA_QUERY).matches;
};

export function resolveSystemThemeMode(mode: string | undefined): ResolvedThemeMode {
  return mode === ThemeMode.Dark || mode === ThemeMode.Light
    ? mode
    : (readBootstrappedColorScheme() ??
        (prefersDarkColorScheme() ? ThemeMode.Dark : ThemeMode.Light));
}

export function resolveThemeMode(
  mode: ThemeMode,
  { presetMode = ThemeMode.Light, systemMode = ThemeMode.Light }: ResolveThemeModeOptions = {},
): ResolvedThemeMode {
  switch (mode) {
    case ThemeMode.Preset:
      return presetMode;
    case ThemeMode.System:
      return systemMode;
    case ThemeMode.Light:
    case ThemeMode.Dark:
      return mode;
  }
}

const dispatch = (curr: ThemeMode, next: ThemeMode) => {
  const storageArea = localStorage;
  const oldValue = JSON.stringify(curr);
  const newValue = JSON.stringify(next);
  const event = new StorageEvent("storage", {
    key: STORAGE_KEY,
    oldValue,
    newValue,
    storageArea,
  });
  window.dispatchEvent(event);
};

/**
 * `useGlobalTheme` returns which theme to pass to the top level `ThemeProvider`, the
 * current `ThemeMode` and functions to change the active theme.
 */
export const useGlobalTheme = (): UseGlobalTheme => {
  const { value: mode, set: setMode } = useLocalStorageValue(STORAGE_KEY, {
    defaultValue: ThemeMode.Preset,
    initializeWithValue: true,
  });
  const { setMode: setColorScheme } = useColorScheme();

  const setTheme = useCallback(
    (newMode: ThemeMode) => {
      const existingMode = mode;
      if (newMode !== existingMode) {
        setMode(newMode);
        setColorScheme(toProviderMode(newMode));
        dispatch(existingMode, newMode);
      }
    },
    [mode, setMode, setColorScheme],
  );

  return useMemo(() => ({ mode, setTheme }), [mode, setTheme]);
};

export function useGlobalThemeMode(): ThemeMode {
  const { value: mode } = useLocalStorageValue(STORAGE_KEY, {
    defaultValue: ThemeMode.Preset,
    initializeWithValue: true,
  });

  return mode;
}

export function useResolvedGlobalThemeMode(
  presetMode: ResolvedThemeMode = ThemeMode.Light,
): ResolvedThemeMode {
  const mode = useGlobalThemeMode();
  const { systemMode } = useColorScheme();

  return resolveThemeMode(mode, {
    presetMode,
    systemMode: resolveSystemThemeMode(systemMode),
  });
}
